/**
 * Proactive offline-data sync.
 *
 * The attorney's connectivity model is inverted from most apps: real internet
 * exists reliably ONLY outside a court day (office, home, anywhere else) —
 * NEVER guaranteed during one, not even opportunistically during a recess
 * (courthouse Wi-Fi is a mix of absent/unreliable). So the app can't wait for
 * the attorney to browse into a feature to warm its cache lazily (today's
 * runtime-caching strategy, see vite.config.ts) — by the time they need a
 * statute or a case, they may already be offline. Instead, the moment real
 * connectivity is detected, pull down everything the courtroom session could
 * need, so a zero-connectivity day is covered regardless of which juror,
 * charge, statute, or rule actually comes up.
 *
 * Runs once per detected online window (throttled so it doesn't re-fetch
 * everything on every page load), fire-and-forget, with toasts so the
 * attorney has visible confirmation the sync actually finished before they
 * lose connectivity.
 */
import { loadMglSearch } from '@/app/mgl';
import { getCaselawDb } from '@/data/caselaw-db-instance';
import { showToast } from '@/ui/overlays';
import { byId } from '@/ui/dom';

const LAST_SYNC_KEY = 'spatchai_offline_sync_at';
const RESYNC_AFTER_MS = 12 * 60 * 60 * 1000; // don't re-hammer the network more than every 12h

/**
 * Render the current sync status into the Case Info page's status line.
 *
 * This used to also show/hide a "#docket-portal-link" beside the docket import
 * according to navigator.onLine. That link is gone — the import window's Step 1
 * opens the portal instead — and the window already swaps its own portal button
 * for an offline notice, so the online/offline handling moved with it.
 */
export function renderSyncStatus(): void {
  const el = byId('offline-sync-status');
  if (!el) return;
  const last = Number(localStorage.getItem(LAST_SYNC_KEY) || 0);
  if (syncing) {
    el.textContent = 'Syncing…';
    return;
  }
  if (!last) {
    el.textContent = navigator.onLine
      ? 'Not yet synced — tap Sync Now before you lose connectivity.'
      : 'Not yet synced, and currently offline — sync when you have internet.';
    return;
  }
  const d = new Date(last);
  const stamp = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  el.textContent = `Last synced ${stamp}${navigator.onLine ? '' : ' — currently offline'}.`;
}

function shouldSync(): boolean {
  if (!navigator.onLine) return false;
  const last = Number(localStorage.getItem(LAST_SYNC_KEY) || 0);
  return Date.now() - last > RESYNC_AFTER_MS;
}

/** Fetch EVERY MGL chapter (not just ones a lookup has touched) so the whole statute corpus is cached. */
async function prefetchAllStatutes(): Promise<void> {
  const mgl = await loadMglSearch();
  if (!mgl) return;
  const search = mgl;
  const chapters = search.getChapters();
  const CONCURRENCY = 8;
  let next = 0;
  async function worker(): Promise<void> {
    for (;;) {
      const i = next++;
      if (i >= chapters.length) return;
      try {
        await search.getChapter(chapters[i].number);
      } catch {
        /* one bad chapter shouldn't stop the rest */
      }
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
}

/** Touch the offline case-law/statute/court-rules SQLite DB so it (and its WASM engine) are cached. */
async function prefetchCaselawDb(): Promise<void> {
  try {
    await getCaselawDb();
  } catch {
    /* best effort */
  }
}

/** Load both on-device OCR engines once so their WASM/model/language-data assets are cached. */
async function prefetchOcrEngines(): Promise<void> {
  try {
    const [{ recognizeText }, { getHandwritingRecognizer }] = await Promise.all([
      import('@/ocr/ocr'),
      import('@/ocr/handwriting')
    ]);
    // A trivial blank canvas is enough to force Tesseract to load its worker + WASM + lang data.
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    await recognizeText(c).catch(() => undefined);
    await getHandwritingRecognizer().catch(() => undefined);
  } catch {
    /* best effort — Scan still lazy-loads these on first real use if this failed */
  }
}

let syncing = false;

/** Run the full offline-readiness sync now. `force` skips the 12h throttle (e.g. a manual "Sync now" button). */
export async function syncOfflineData(force = false): Promise<void> {
  if (syncing) return;
  if (!force && !shouldSync()) return;
  syncing = true;
  renderSyncStatus();
  showToast('Syncing case law, statutes, rules, and OCR for offline court use…', 2500);
  try {
    await Promise.all([prefetchAllStatutes(), prefetchCaselawDb(), prefetchOcrEngines()]);
    localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
    showToast('Offline data ready — this device is prepared for a no-connectivity court day.', 4000);
  } finally {
    syncing = false;
    renderSyncStatus();
  }
}

/** Wire the sync to run on load (if already online) and whenever connectivity returns. */
export function installOfflineSync(): void {
  if (navigator.onLine) void syncOfflineData();
  window.addEventListener('online', () => {
    renderSyncStatus();
    void syncOfflineData();
  });
  window.addEventListener('offline', renderSyncStatus);
}
